import { colors } from '../../theme/colors'
import { AllColors, Color, ColorShadesKeys, ColorWithShade } from '../../types/Colors'

export const DEFAULT_SHADE: ColorShadesKeys = 500

type Shade = ColorShadesKeys | Partial<Record<Color | 'fallback', ColorShadesKeys>>

/**
 * Returns the CSS variable corresponding to a color with an optional shade.
 * @param color - The color name or color name with shade (e.g., 'blue' or 'red-200').
 * @param fallbackShade - Optional. The specific shade value for the color. If not provided, it defaults to 500.
 * @returns The CSS variable representing the color with the specified shade.
 */
const getColorVar = (color: AllColors, baseFallbackShade: Shade = DEFAULT_SHADE) => {
  const isHex = /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(color)
  if (isHex) return color
  if (color === 'transparent') return color

  const [baseColor, specificShade] = color.split('-')
  const fallbackShade =
    typeof baseFallbackShade === 'number'
      ? baseFallbackShade
      : baseFallbackShade[baseColor as Color] ?? baseFallbackShade.fallback
  const selectedShade = specificShade ?? fallbackShade ?? DEFAULT_SHADE

  return `var(--${baseColor}-${selectedShade})`
}

export default getColorVar

/**
 * This function is intended to use with styled components
 * @param prop - Color prop name, eg: "color" or "backgroundColor"
 * @param shade - Optional. The specific shade value for the color. If not provided, it defaults to 500.
 * @param fallbackColor - Optional. A color to be used when there is no color prop set
 * @returns A function to be used at the styled component
 */
export const colorFromProp = (colorProp: string, shade?: Shade, fallbackColor?: AllColors | {}) => (props: unknown) => {
  const color = ((props as Record<string, unknown>)[colorProp] ?? fallbackColor ?? 'gray') as AllColors
  return getColorVar(color, shade)
}

export const getColorVarHex = (selectedColor?: AllColors, baseFallbackShade = 500) => {
  if (!selectedColor) return undefined
  if (selectedColor === 'transparent') return selectedColor

  const [baseColor, specificShade] = selectedColor.split('-')
  const selectedShade = (specificShade ?? baseFallbackShade) as ColorShadesKeys

  const colorShades = Object.entries(colors).find(([color]) => color === baseColor)?.[1]
  return colorShades?.[selectedShade]
}

export const getColorVarByHex = (hex?: string): ColorWithShade | undefined => {
  if (hex) {
    const normalizedHex = hex.toUpperCase()
    for (const [colorName, colorShades] of Object.entries(colors)) {
      for (const [shade, colorHex] of Object.entries(colorShades)) {
        if (colorHex === normalizedHex) {
          return `${colorName}-${shade}` as ColorWithShade
        }
      }
    }
  }

  return undefined
}
